import { noop } from "@zetalabs/core";

type Modifier = "ctrl" | "meta" | "alt" | "shift";

type KeyboardEventHandler = (e: KeyboardEvent) => void;
type UnregisterCallback = () => void;

interface Keys {
	value: string;
	modifiers: Modifier[];
}

export interface Shortcut {
	key: Keys;
	handler: KeyboardEventHandler;
	global?: boolean;
	pop?: boolean;
	force?: boolean;
}

type Layer = {
	type: "default";
	name: string;
	shortcuts: Record<string, Shortcut>;
};

export class KeyboardShortcutsManager {
	private layers = new Map<string, Layer | undefined>();
	private stack: (string | undefined)[] = [];

	private isActive = false;
	private globalShortcuts: Record<string, Shortcut> = {};

	public register(layer: Layer): UnregisterCallback {
		this.layers.set(layer.name, layer);
		this.stack.push(layer.name);

		return () => {
			this.unregister(layer.name);

			for (const [key, shortcut] of Object.entries(layer.shortcuts)) {
				if (shortcut.global) {
					delete this.globalShortcuts[key];
				}
			}
		};
	}

	public merge(layerName: string, shortcuts: Shortcut[]) {
		const layer = this.layers.get(layerName);

		if (!layer) return;

		for (const shortcut of shortcuts) {
			const hashedKey = this.hash(shortcut.key);

			if (shortcut.global) {
				this.globalShortcuts[hashedKey] = shortcut;
			} else {
				layer.shortcuts[hashedKey] = shortcut;
			}
		}
	}

	public registerGlobalShortcut(
		key: string,
		modifiers: Modifier[],
		handler: (e: KeyboardEvent) => void,
	): UnregisterCallback {
		const hashedKey = this.hash({ value: key, modifiers });

		this.globalShortcuts[hashedKey] = {
			key: { value: key, modifiers },
			handler,
		};

		return () => {
			delete this.globalShortcuts[hashedKey];
		};
	}

	public registerShortcuts(
		shortcuts: Shortcut[],
		layerName: string,
	): UnregisterCallback {
		const layer: Layer = {
			type: "default",
			name: layerName,
			shortcuts: {},
		};
		if (this.layers.has(layerName)) {
			this.merge(layerName, shortcuts);
		}

		for (const shortcut of shortcuts) {
			const hashedKey = this.hash(shortcut.key);

			if (shortcut.global) {
				this.globalShortcuts[hashedKey] = shortcut;
			} else {
				layer.shortcuts[hashedKey] = shortcut;
			}
		}

		if (!this.isActive) {
			this.isActive = true;
			document.addEventListener("keydown", this.handleKeyDown);
		}

		if (Object.keys(layer.shortcuts).length > 0) {
			return this.register(layer);
		}

		return noop;
	}

	private unregister(layerName?: string) {
		if (layerName) {
			this.layers.delete(layerName);
		}

		if (
			Object.keys(this.layers).length === 0 &&
			Object.keys(this.globalShortcuts).length === 0
		) {
			document.removeEventListener("keydown", this.handleKeyDown);
			this.isActive = false;
		}

		// Maybe this should be the last element
		const index = this.stack.indexOf(layerName);

		if (index !== -1) {
			this.stack.splice(index, 1);
		}
	}

	public reset() {
		this.stack = [];
		this.layers.clear();
	}

	public dispose() {
		document.removeEventListener("keydown", this.handleKeyDown);
		this.isActive = false;
	}

	private handleKeyDown = (event: KeyboardEvent) => {
		const keys = this.parseKeyboardEvent(event);
		const hashedKey = this.hash(keys);

		const globalShortcut = this.globalShortcuts[hashedKey];

		if (globalShortcut) {
			if (this.block() && !globalShortcut?.force) return;

			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();

			globalShortcut.handler(event);

			return;
		}

		const layerName = this.stack[this.stack.length - 1];

		if (layerName) {
			const layer = this.layers.get(layerName);

			const shortcut = layer?.shortcuts[hashedKey];

			if (this.block() && !shortcut?.force) return;

			if (shortcut) {
				event.preventDefault();
				event.stopPropagation();
				event.stopImmediatePropagation();

				shortcut.handler(event);

				if (shortcut.pop) {
					this.stack.pop();
				}
				return;
			}
		}
	};

	private block(): boolean {
		const el = document.activeElement;
		if (!el) return false;

		return (
			el.tagName === "INPUT" ||
			el.tagName === "TEXTAREA" ||
			el.getAttribute("contenteditable") === "true"
		);
	}

	private parseKeyboardEvent(event: KeyboardEvent): Keys {
		const key = event.key.toLowerCase();
		const modifiers: Modifier[] = [];

		if (event.ctrlKey) modifiers.push("ctrl");
		if (event.metaKey) modifiers.push("meta");
		if (event.altKey) modifiers.push("alt");

		if (this.requiresShift(key)) {
			if (event.shiftKey) modifiers.push("shift");
		}

		return { value: key, modifiers };
	}

	private hash(key: Keys): string {
		return [key.value.toLowerCase(), ...key.modifiers.sort()].join("-");
	}

	// NOTE: (bwilytsch): This helps with special characters like `#`, `@`, `!`, etc.
	private requiresShift(key: string): boolean {
		return key.toLowerCase() !== key.toUpperCase();
	}
}
